import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { t } from '@aquilixshop/i18n';
import { ALIGN_OPTIONS, marginFields, visibilityField, weightField, } from '../_shared/blockFields.js';
import { marginStyle, num, readWeight, str, visibilityCss } from '../_shared/blockRender.js';
import { useProductState } from '../_shared/productContext.js';
import { sanitizeRichtextHtml } from '../../sanitize/sanitizeRichtextHtml.js';
/**
 * Shrine `product_quantity-selector` — CLASSIC stepper (the default `enable_quantity_discounts = false`).
 *
 * Config-by-config parity for the CLASSIC mode. Shrine ships 161 settings on this block, but ~150 of
 * them belong exclusively to the OTHER, mutually-exclusive rendering mode (`enable_quantity_discounts`
 * = the "BUNDLE & SAVE" / quantity-breaks widget: headline, style, preselected, prices_layout,
 * display_selected_indicator, border_*, the theme-vs-custom color systems, the 4 quantity-break offers
 * option_1..4_*, the 3 upsell/gift slots upsell_1..3_*, the variant selectors, the per-offer images,
 * and the vertical-style settings). That widget depends on Shopify's automatic-discount engine and is a
 * documented large sub-project — it stays DEFERRED here (see DEFERRED below), so the classic stepper is
 * what renders.
 *
 * The handful of Shrine settings that DO govern the classic stepper are mapped here:
 *  - `full_width_classic`  → our existing `fullWidth` id (PRESERVED, not renamed).
 *  - `margin_top` / `margin_bottom` → marginFields (PRESERVED).
 *  - variant `quantity_rule.min` / `.max` / `.increment` (Shopify Liquid objects that drive the classic
 *    stepper's clamping/stepping) → exposed as authoring settings `minQuantity` / `maxQuantity` /
 *    `stepQuantity` so a prepaid/COD store can set order minimums, caps and case-pack increments.
 *  - a quantity `label` + a "show label" toggle — Shrine's classic stepper carries a label; ours was a
 *    hard-coded "Quantité", now authorable.
 *
 * DEFERRED (justified):
 *  - `enable_quantity_discounts` + the entire BUNDLE & SAVE / quantity-breaks mode and every setting that
 *    only applies inside it — large sub-project, depends on Shopify automatic-discounts; out of scope for
 *    this round. The classic stepper stands in for the default (off) state.
 *  - Shopify-CDN image_picker / product-picker resource fields, theme color-scheme `color`/`color_background`
 *    gradients, and `update_pdp_prices` / `update_prices` (Shopify section-block plumbing + currency-app
 *    coupling) — Shopify-platform settings with no classic-stepper meaning.
 */
export const quantityBlockSchema = {
    type: 'quantity',
    name: 'Quantity selector',
    max: 1,
    fields: [
        visibilityField,
        // ── Label (Shrine classic stepper carries a label) ───────────────────────
        {
            id: 'showLabel',
            type: 'checkbox',
            label: 'Show label',
            group: 'General',
            default: true,
        },
        {
            id: 'label',
            type: 'text',
            label: 'Label',
            group: 'General',
            info: 'Text shown next to the stepper.',
            default: 'Quantité',
            defaultKey: 'cart.quantity',
            maxLength: 40,
            visibleWhen: { field: 'showLabel', equals: true },
        },
        {
            id: 'labelPosition',
            type: 'select',
            label: 'Label position',
            group: 'General',
            info: 'Beside the stepper, or above it — the stepper then gets a row of its own.',
            default: 'inline',
            options: [
                { value: 'inline', label: 'Beside the stepper' },
                { value: 'above', label: 'On its own line' },
            ],
            visibleWhen: { field: 'showLabel', equals: true },
        },
        {
            ...weightField,
            group: 'General',
            info: 'Applies to the label and to the quantity number.',
        },
        { id: 'fullWidth', type: 'checkbox', label: 'Full width', group: 'General', default: false },
        {
            id: 'alignment',
            type: 'select',
            label: 'Alignment',
            group: 'General',
            info: 'Ignored when Full width is on (the stepper then fills the row).',
            default: 'left',
            options: ALIGN_OPTIONS,
        },
        // ── Quantity rule (Shopify variant quantity_rule.min/max/increment) ───────
        {
            id: 'minQuantity',
            type: 'number',
            label: 'Minimum quantity',
            group: 'Quantity rule',
            info: 'Smallest quantity a customer can order.',
            default: 1,
            min: 1,
            step: 1,
        },
        {
            id: 'maxQuantity',
            type: 'number',
            label: 'Maximum quantity',
            group: 'Quantity rule',
            info: 'Largest quantity a customer can order. 0 = no limit.',
            default: 0,
            min: 0,
            step: 1,
        },
        {
            id: 'stepQuantity',
            type: 'number',
            label: 'Increment',
            group: 'Quantity rule',
            info: 'Quantity step (e.g. case packs sold in multiples).',
            default: 1,
            min: 1,
            step: 1,
        },
        ...marginFields(15, 15),
    ],
};
/** Alignment select → flex `justify-content` for the stepper row. */
const JUSTIFY = { left: 'flex-start', center: 'center', right: 'flex-end' };
const qtyBtn = {
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    color: 'inherit',
    fontSize: 'var(--aqx-theme-text-lg, 20px)',
    inlineSize: '40px',
    blockSize: '40px',
};
/**
 * Clamp a candidate quantity to the Shrine quantity_rule (min / max / increment). `max` of 0 means no
 * upper limit. Values are snapped to the nearest valid increment at or above `min`.
 */
function clampQty(value, min, max, step) {
    const lo = Math.max(1, min);
    const s = Math.max(1, step);
    let q = Math.max(lo, value);
    // Snap to lo + k*step.
    const offset = q - lo;
    q = lo + Math.round(offset / s) * s;
    if (q < lo)
        q = lo;
    if (max > 0 && q > max) {
        // Largest valid increment ≤ max.
        const k = Math.floor((max - lo) / s);
        q = lo + Math.max(0, k) * s;
    }
    return q;
}
/**
 * Quantity stepper [−][n][+], bound to the shared quantity state, clamped to the quantity rule.
 *
 * This block is stepper-ONLY. The per-product volume-discount offer tiles (feature 0026-07, `context.activeOffer`)
 * used to render here in place of the stepper; they now live at the TOP of the `cod-form` block (the COD order
 * form) via the shared `OfferTiles` component — the founder-validated placement. The stepper's preselected
 * starting quantity is still seeded from the offer's preselect tier (in `productContext.tsx#initialQuantity`),
 * independent of which block draws the tiles.
 */
const QuantityBlock = ({ block, context }) => {
    const ps = useProductState();
    if (!ps)
        return null;
    const bs = block.settings;
    const cls = `aqx-qt-${block.id}`;
    const { quantity, setQuantity } = ps;
    const full = bs.fullWidth === true;
    const showLabel = bs.showLabel !== false;
    const labelText = str(bs.label, t(context, 'cart.quantity'));
    const min = num(bs.minQuantity, 1);
    const max = num(bs.maxQuantity, 0);
    const step = num(bs.stepQuantity, 1);
    const align = JUSTIFY[str(bs.alignment, 'left')] ?? 'flex-start';
    // Label ABOVE the stepper: the stepper then owns its own row, which is what a wide `Full width`
    // stepper (or a long label) needs. `inline` keeps the historical side-by-side row, so an untouched
    // block renders byte-identically.
    const stacked = showLabel && str(bs.labelPosition, 'inline') === 'above';
    // Shared "Font weight" (`''` = keep what the block already used: 600 on the label, inherited on the
    // number and the [−]/[+]).
    const weight = readWeight(bs);
    const vis = visibilityCss(cls, bs.visibility);
    return (_jsxs(_Fragment, { children: [vis ? _jsx("style", { dangerouslySetInnerHTML: { __html: vis } }) : null, _jsxs("div", { className: cls, style: {
                    display: 'flex',
                    // Column = the label sits on its own line above the stepper. In a column, `alignItems` is what
                    // places the children horizontally (the row's `justifyContent` no longer does).
                    flexDirection: stacked ? 'column' : 'row',
                    alignItems: stacked ? align : 'center',
                    justifyContent: stacked ? 'flex-start' : align,
                    gap: stacked ? 'var(--aqx-theme-space-2, 8px)' : 'var(--aqx-theme-space-3, 12px)',
                    ...(weight ? { fontWeight: weight } : {}),
                    ...marginStyle(bs),
                }, children: [showLabel ? (_jsx("span", { style: { fontSize: 'var(--aqx-theme-text-sm, 14px)', fontWeight: weight || 600 }, dangerouslySetInnerHTML: { __html: sanitizeRichtextHtml(labelText) } })) : null, _jsxs("div", { style: {
                            display: 'flex',
                            // `flexGrow` fills the ROW; in a column it would stretch the stepper VERTICALLY instead, so
                            // full width has to be asked for on the cross axis.
                            flexGrow: full && !stacked ? 1 : 0,
                            ...(full && stacked ? { alignSelf: 'stretch' } : {}),
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            border: '1px solid var(--aqx-scheme-border)',
                            borderRadius: 'var(--aqx-theme-radius-input, 6px)',
                        }, children: [_jsx("button", { type: "button", "aria-label": t(context, 'a11y.decrease_quantity'), onClick: () => setQuantity((q) => clampQty(q - step, min, max, step)), style: qtyBtn, children: "\u2212" }), _jsx("span", { "aria-live": "polite", style: { minInlineSize: '2ch', textAlign: 'center' }, children: quantity }), _jsx("button", { type: "button", "aria-label": t(context, 'a11y.increase_quantity'), onClick: () => setQuantity((q) => clampQty(q + step, min, max, step)), style: qtyBtn, children: "+" })] })] })] }));
};
export const quantityBlock = {
    type: 'quantity',
    schema: quantityBlockSchema,
    Component: QuantityBlock,
    meta: { label: 'Quantity selector' },
};
//# sourceMappingURL=index.js.map